import { isAutoInvoicePartner } from "@/constants/partner"

/**
 * Query params partners pass when opening the WebView.
 * All four invoice fields are required.
 *
 * Prefer short aliases (`inv_*`); long names kept for backward compatibility.
 */
export const SESSION_INVOICE_QUERY_PARAMS = {
  companyName: ["inv_name", "invoice_company_name"],
  companyTaxNo: ["inv_tax", "invoice_company_tax_no"],
  companyAddress: ["inv_addr", "invoice_company_address"],
  email: ["inv_email", "invoice_email_receiver", "invoice_email"],
} as const

const INVOICE_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const INVOICE_TAX_CODE_PATTERN = /^([0-9]{10})(|)(-[0-9]{3}|)$/

export interface SessionInvoiceParams {
  company_name: string
  company_tax_no: string
  company_address: string
  email_receiver: string
}

function getFirstSearchParam(
  searchParams: URLSearchParams,
  keys: readonly string[]
): string | null {
  for (const key of keys) {
    const value = searchParams.get(key)?.trim()
    if (value) return value
  }
  return null
}

export function parseSessionInvoiceFromSearchParams(
  searchParams: URLSearchParams
): SessionInvoiceParams | null {
  const company_name = getFirstSearchParam(
    searchParams,
    SESSION_INVOICE_QUERY_PARAMS.companyName
  )
  const company_tax_no = getFirstSearchParam(
    searchParams,
    SESSION_INVOICE_QUERY_PARAMS.companyTaxNo
  )
  const company_address = getFirstSearchParam(
    searchParams,
    SESSION_INVOICE_QUERY_PARAMS.companyAddress
  )
  const email_receiver = getFirstSearchParam(
    searchParams,
    SESSION_INVOICE_QUERY_PARAMS.email
  )

  if (
    !company_name ||
    !company_tax_no ||
    !company_address ||
    !email_receiver ||
    !INVOICE_TAX_CODE_PATTERN.test(company_tax_no) ||
    !INVOICE_EMAIL_PATTERN.test(email_receiver)
  ) {
    return null
  }

  return {
    company_name,
    company_tax_no,
    company_address,
    email_receiver,
  }
}

export function createSessionInvoicePayload(
  userId: number,
  params: SessionInvoiceParams
) {
  return {
    invoice: {
      company_name: params.company_name,
      company_tax_no: params.company_tax_no,
      company_address: params.company_address,
      email_receiver: params.email_receiver,
      user_id: userId,
    },
  }
}

export function shouldAutoApplyInvoice(
  partnerIdentifier: string | null,
  sessionInvoiceActive: boolean
): boolean {
  return sessionInvoiceActive || isAutoInvoicePartner(partnerIdentifier)
}
